import { ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { DefaultPage } from '@/pages/dashboards';
import { AccountUserProfilePage } from '@/pages/account';
import {
  RolesManagementPage,
  RolePermissionsPage,
} from '@/pages/roles-management';
import { EmailProtectedRoute } from './EmailProtectedRoute';
import {
  ServiceManagement,
  ServiceProviderDetailsView,
  ServiceProviderRequests,
} from '@/pages/service-management';
import { QAManagement } from '@/pages/QA-management';
import { Misc } from '@/pages/misc';
import { CategoryManagement } from '@/pages/category-management/CategoryManagement';
import { AuthPage } from '@/auth';
import { RequireAuth } from '@/auth/RequireAuth';
import { Demo1Layout } from '@/layouts/demo1';
import { ErrorsRouting } from '@/errors';
import { ChangePassword } from '@/pages/account/home/change-password';
import { UserKYC, AddKYC, EditKYC, ViewKYC } from '@/pages/user-kyc';
import { UserManagement } from '@/pages/user-management';
import { AuditLogsManagement } from '@/pages/audit-logs';
import { BoatManagement } from '@/pages/boat-management';
import { HarborManagement } from '@/pages/habor-management/HarborManagement';
import { UserFeedbackManagement } from '@/pages/user-feedback';
import UserFeedbackDetailsPage from '@/pages/user-feedback/UserFeedbackDetailsPage';
import {
  AboutUs,
  PrivacyPolicy,
  TermsAndConditions,
  Accessibility,
  FAQ,
  Licensing,
  CookieConsent,
} from '@/pages/cms';
import {
  ArticlesListing,
  ArticleForm,
  ArticleVersions,
  ArticleView,
} from '@/pages/article-management';
import {
  HelpdeskFAQListing,
  HelpdeskFAQForm,
  HelpdeskFAQView,
} from '@/pages/helpdesk-faq';
import {
  PlatformSettingListing,
  PlatformSettingForm,
} from '@/pages/platform-setting';
import { YachtManagement } from '@/pages/yatch-management/YatchManagement';
import { PermissionRoute } from './PermissionRoute';
import RegionListings from '@/pages/region-management/RegionListings';
import { ClubMembershipRequests } from '@/pages/club-membership-request/ClubMembershipRequest';
import { ClubMembers } from '@/pages/club-members/ClubMembers';
import { BerthManagement } from '@/pages/berth-management/BerthManagement';
import { BerthCategoryManagement } from '@/pages/berth-category/BerthCategoryManagement';
import { BookingManagement } from '@/pages/booking-management/BookingManagement';
import HarborDetails from '@/pages/habor-management/HarborDetails';
import { HarborForm } from '@/pages/habor-management/HarborForm';
import { HarborBulkImport } from '@/pages/habor-management/HarborBulkImport';
import { HarborBulkUpdate } from '@/pages/habor-management/HarborBulkUpdate';
import { AddonManagement } from '@/pages/addon-management/AddonManagement';
import { PromoManagement } from '@/pages/promo-management/PromoManagement';
import PromoFormPage from '@/pages/promo-management/PromoFormPage';
import { CommunityManagement } from '@/pages/community-management/CommunityManagement';
import CommunityDetails from '@/pages/community-management/CommunityDetails';
import BookingDetails from '@/pages/booking-management/BookingDetails';
import { BookingManagementOutlet } from '@/pages/booking-management';
import { BerthConflictListings } from '@/pages/booking-management/BerthConflictListings';
import { PierManagement } from '@/pages/group-management/PierManagement';
import YatchClubDetailsView from '@/pages/yatch-management/YatchClubDetailsView';
import { ExternalServicesOutlet } from '@/pages/external-services';
import { ExternalServiceManagement } from '@/pages/external-services/ExternalServiceManagement';
import { NotificationManagement } from '@/pages/notification-management/NotificationManagement';
import RegistrationManagement from '@/pages/club-registration/RegistrationManagement';
import RoutingManagement from '@/pages/routing-management/RoutingManagement';
import RoutingDebugger from '@/pages/routing-management/RoutingDebugger';
import RouteDetails from '@/pages/routing-management/components/RouteDetails';
import ManualRouteDetails from '@/pages/routing-management/components/ManualRouteDetails';
import VersionDetails from '@/pages/routing-management/components/VersionDetails';
import VersionSources from '@/pages/routing-management/components/VersionSources';
import ManualRouteModal from '@/pages/routing-management/modal/ManualRouteModal';
import ThemeRoutesManagement from '@/pages/theme-routes/ThemeRoutesManagement';
import ThemeRouteFormPage from '@/pages/theme-routes/ThemeRouteFormPage';
import ThemeRouteVersions from '@/pages/theme-routes/ThemeRouteVersions';
import ThemeRouteApprovalsPage from '@/pages/theme-routes/ThemeRouteApprovalsPage';
import OrganizationManagement from '@/pages/organization-management/OrganizationManagement';
import { YatchModal } from '@/pages/yatch-management/YatchModal';
import { SubscriptionManagement } from '@/pages/subscription-management';
import SubscriptionPermissionsPage from '@/pages/subscription-management/SubscriptionPermissionsPage';
import SubscriptionPayment from '@/pages/subscription-management/SubscriptionPayment';
import PaymentSuccess from '@/pages/subscription-management/PaymentSuccess';
import { ServiceRequestManagement } from '@/pages/service-request-management';
import {
  HarborDeletionManagement,
  HarborRequestManagement,
} from '@/pages/habor-management';
import { IconImagesListings } from '@/pages/Icon-Images-management/IconImagesListings';
import { KPIManagement } from '@/pages/kpi-management/KPIManagement';
import { LegalCompliancePage } from '@/pages/legal-compliance/LegalCompliancePage';
import DeploymentsListing from '@/pages/deployment-management/DeploymentsListing';
import DeploymentDetails from '@/pages/deployment-management/DeploymentDetails';
import {
  ClubBookingListings,
  ClubBookingDetails,
} from '@/pages/club-booking-management';
import { EmailTemplateListings } from '@/pages/email-template-management/EmailTemplateListings';
import EmailTemplateDetails from '@/pages/email-template-management/EmailTemplateDetails';
import { NotificationTemplateListings } from '@/pages/notification-template-management/NotificationTemplateListings';
import NotificationTemplateDetails from '@/pages/notification-template-management/NotificationTemplateDetails';
import { PortWatchShiftsDetails } from '@/pages/club-resource/PortWatchShiftsDetails';
import AnnualInspectionPage from '@/pages/club-resource/AnnualInspectionPage';
import { AdvertiseManagement } from '@/pages/advertise-management/AdvertiseManagement';
import AdvertiseForm from '@/pages/advertise-management/AdvertiseForm';
import { ProductVatConfigManagement } from '@/pages/product-vat-config/ProductVatConfigManagement';
import ProductVatConfigForm from '@/pages/product-vat-config/ProductVatConfigForm';
import DomesticVatConfigManagement from '@/pages/domestic-vat-config/DomesticVatConfigManagement';
import DomesticVatConfigForm from '@/pages/domestic-vat-config/DomesticVatConfigForm';
import BusinessEntityVatConfigManagement from '@/pages/business-entity-vat-config/BusinessEntityVatConfigManagement';
import BusinessEntityVatConfigForm from '@/pages/business-entity-vat-config/BusinessEntityVatConfigForm';
import { PenaltyConfigManagement } from '@/pages/penalty-config/PenaltyConfigManagement';
import { PspFeeConfigManagement } from '@/pages/psp-fee-config/PspFeeConfigManagement';
import { PoiRequestManagement } from '@/pages/poi-request-management/PoiRequestManagement';
import EventForm from '@/pages/community-management/components/EventForm';
import DiscountTrackingReports from '@/pages/discount-tracking-reports/DiscountTrackingReports';
import HistoricalTrends from '@/pages/historical-trends/HistoricalTrends';
import RevenueReport from '@/pages/revenue-report/RevenueReport';
import CancellationReports from '@/pages/cancellation-reports/CancellationReports';
import ServiceProviderFormPage from '@/pages/service-provider-management/ServiceProviderFormPage';
import Transactions from '@/pages/transactions/Transactions';
import TransactionDetails from '@/pages/transactions/TransactionDetails';
import SubscriptionTransactions from '@/pages/subscription-transactions/SubscriptionTransactions';
import SubscriptionTransactionDetails from '@/pages/subscription-transactions/SubscriptionTransactionDetails';
import Settlements from '@/pages/settlements/Settlements';
import SettlementDetails from '@/pages/settlements/SettlementDetails';
import ServiceFormPage from '@/pages/service-management/ServiceFormPage';
import BoatFormPage from '@/pages/boat-management/BoatFormPage';
import ServiceBookingDetails from '@/pages/service-management/ServiceBookingDetails';
import { EmailLayoutListing } from '@/pages/template-layout-management/EmailLayoutListing';
import EmailLayoutView from '@/pages/template-layout-management/EmailLayoutView';
import { SystemAnnouncementsManagement } from '@/pages/system-announcements/SystemAnnouncementsManagement';
import { SystemAnnouncementsArchivedManagement } from '@/pages/system-announcements/SystemAnnouncementsArchivedManagement';
import SystemAnnouncementsForm from '@/pages/system-announcements/SystemAnnouncementsForm';
import { SystemAnnouncementsRecipients } from '@/pages/system-announcements/SystemAnnouncementsRecipients';
import AccountingExports from '@/pages/netvisor/AccountingExports';
import AccountingExportDetails from '@/pages/netvisor/AccountingExportDetails';
import {
  ServiceModerationManagement,
  ServiceModerationDetails,
} from '@/pages/service-moderation';
import { BookingReminderConfiguration } from '@/pages/configuration/BookingReminderConfiguration';
import { CommissionPolicyConfiguration } from '@/pages/configuration/CommissionPolicyConfiguration';
import { SubscriptionTrialConfiguration } from '@/pages/configuration/SubscriptionTrialConfiguration';
import { GuestVisibilityConfiguration } from '@/pages/guest-visibility/GuestVisibilityConfiguration';
import { GuestBookingGovernanceConfiguration } from '@/pages/guest-booking-governance/GuestBookingGovernanceConfiguration';
import { EntitlementGrantManagement } from '@/pages/entitlement-grants/EntitlementGrantManagement';
import { EntitlementGrantConfiguration } from '@/pages/entitlement-grants/EntitlementGrantConfiguration';
import HarborCommissionPolicyPage from '@/pages/commission-policy/HarborCommissionPolicyPage';
import ServiceProviderCommissionPolicyPage from '@/pages/commission-policy/ServiceProviderCommissionPolicyPage';
import { DMOManagement } from '@/pages/dmo-management';
import RegisterDMOUserPage from '@/pages/dmo-management/RegisterDMOUserPage';
import DMOUserDetails from '@/pages/dmo-management/DMOUserDetails';
import PublicDMORegistrationPage from '@/pages/dmo-management/PublicDMORegistrationPage';
import DMOStaffManagement from '@/pages/dmo-management/DMOStaffManagement';
import { MembersRosterForm, MemberBoatsListing } from '@/pages/members-roster';
import {
  TestimonialsManagement,
  TestimonialFormPage,
} from '@/pages/testimonial-management';
import { KYCDetailsView, KYCManagement } from '@/pages/kyc-management';
import NotificationPreferences from '@/pages/notification-management/NotificationPreferences';
import { EnvironmentVariablesListing } from '@/pages/environment-variables/EnvironmentVariablesListing';
import { EnvironmentVariableForm } from '@/pages/environment-variables/EnvironmentVariableForm';
import {
  ListValueFormPage,
  ListValueManagement,
} from '@/pages/list-value-management';
import CommunityInvitationAcceptPage from '@/pages/community-invitations/CommunityInvitationAcceptPage';
import { SyntheticCanary } from '@/pages/synthetic-canary';

const ManualRoutePage = () => {
  return <ManualRouteModal />;
};

const AppRoutingSetup = (): ReactElement => {
  return (
    <Routes>
      <Route element={<RequireAuth />}>
        <Route element={<Demo1Layout />}>
          <Route
            path="/"
            element={
              <PermissionRoute path="/">
                <DefaultPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/user-management"
            element={
              <PermissionRoute path="/user-management">
                <UserManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/roles-management"
            element={
              <EmailProtectedRoute>
                <PermissionRoute path="/roles-management">
                  <RolesManagementPage />
                </PermissionRoute>
              </EmailProtectedRoute>
            }
          />
          <Route
            path="/roles-management/permissions/:roleId"
            element={
              <EmailProtectedRoute>
                <PermissionRoute path="/roles-management">
                  <RolePermissionsPage />
                </PermissionRoute>
              </EmailProtectedRoute>
            }
          />
          <Route
            path="/user-logs"
            element={
              <PermissionRoute path="/user-logs">
                <AuditLogsManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/club-management"
            element={
              <PermissionRoute path="/club-management">
                <YachtManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/club-management/create"
            element={
              <PermissionRoute path="/club-management/create">
                <YatchModal mode="create" />
              </PermissionRoute>
            }
          />
          <Route
            path="/club-management/edit/:id"
            element={
              <PermissionRoute path="/club-management/edit/:id">
                <YatchModal mode="edit" />
              </PermissionRoute>
            }
          />
          <Route
            path="/club-registrations"
            element={
              <PermissionRoute path="/club-registrations">
                <RegistrationManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/club-management/:id"
            element={
              <PermissionRoute path="/club-management/:id">
                <YatchClubDetailsView />
              </PermissionRoute>
            }
          />
          <Route
            path="/club-management/:id/roster/add"
            element={<MembersRosterForm mode="create" />}
          />
          <Route
            path="/club-management/:id/roster/edit/:rosterId"
            element={<MembersRosterForm mode="edit" />}
          />
          <Route
            path="/club-management/:id/roster/view/:rosterId"
            element={<MembersRosterForm mode="view" />}
          />
          <Route
            path="/club-management/:id/roster/:rosterId/boats"
            element={<MemberBoatsListing />}
          />
          <Route
            path="/club-requests"
            element={
              <PermissionRoute path="/club-requests">
                <ClubMembershipRequests />
              </PermissionRoute>
            }
          />

          <Route
            path="/club-members"
            element={
              <PermissionRoute path="/club-members">
                <ClubMembers />
              </PermissionRoute>
            }
          />
          <Route
            path="/export-logs"
            element={
              <PermissionRoute path="/export-logs">
                <AuditLogsManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/delete-logs"
            element={
              <PermissionRoute path="/delete-logs">
                <AuditLogsManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/boat-management"
            element={
              <PermissionRoute path="/boat-management">
                <BoatManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/boats/create"
            element={<BoatFormPage mode="create" />}
          />
          <Route
            path="/boats/edit/:id"
            element={<BoatFormPage mode="edit" />}
          />
          <Route
            path="/boats/view/:id"
            element={<BoatFormPage mode="view" />}
          />
          <Route
            path="/habor-management"
            element={
              <PermissionRoute path="/habor-management">
                <HarborManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/harbors/deletion-requests"
            element={
              <PermissionRoute path="/harbors/deletion-requests">
                <HarborDeletionManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/harbors/requests"
            element={
              <PermissionRoute path="/harbors/requests">
                <HarborRequestManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/berth-management"
            element={
              <PermissionRoute path="/berth-management">
                <BerthManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/berth-management"
            element={
              <PermissionRoute path="/berth-management">
                <BerthManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/service-request-management"
            element={
              <PermissionRoute path="/service-request-management">
                <ServiceRequestManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/poi-request-management"
            element={
              <PermissionRoute path="/poi-request-management">
                <PoiRequestManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/harbors/create"
            element={
              <PermissionRoute path="/harbors/create">
                <HarborForm mode="create" />
              </PermissionRoute>
            }
          />
          <Route
            path="/harbors/bulk-import"
            element={
              <PermissionRoute path="/harbors/bulk-import">
                <HarborBulkImport />
              </PermissionRoute>
            }
          />
          <Route
            path="/harbors/bulk-update"
            element={
              <PermissionRoute path="/harbors/bulk-update">
                <HarborBulkUpdate />
              </PermissionRoute>
            }
          />
          <Route
            path="/berth-category-management"
            element={
              <PermissionRoute path="/berth-category-management">
                <BerthCategoryManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/booking-management"
            element={<BookingManagementOutlet />}
          >
            <Route
              index
              element={
                <PermissionRoute path="/booking-management">
                  <BookingManagement />
                </PermissionRoute>
              }
            />
            <Route
              path="berth-conflicts"
              element={
                <PermissionRoute path="/booking-management">
                  <BerthConflictListings />
                </PermissionRoute>
              }
            />
            <Route
              path=":bookingId/edit"
              element={
                <PermissionRoute path="/booking-management">
                  <BookingDetails />
                </PermissionRoute>
              }
            />
            <Route
              path=":bookingId"
              element={
                <PermissionRoute path="/booking-management">
                  <BookingDetails />
                </PermissionRoute>
              }
            />
          </Route>
          <Route
            path="/notification-management"
            element={<NotificationManagement />}
          />
          <Route
            path="/notification-preferences"
            element={
              <PermissionRoute path="/notification-preferences">
                <NotificationPreferences />
              </PermissionRoute>
            }
          />
          <Route path="/external-services" element={<ExternalServicesOutlet />}>
            <Route
              index
              element={
                <PermissionRoute path="/external-services">
                  <ExternalServiceManagement />
                </PermissionRoute>
              }
            />
            <Route
              path=":bookingId"
              element={
                <PermissionRoute path="/booking-management">
                  <BookingDetails />
                </PermissionRoute>
              }
            />
          </Route>
          <Route
            path="/harbors/edit"
            element={
              <PermissionRoute path="/harbors/edit">
                <HarborForm mode="edit" />
              </PermissionRoute>
            }
          />
          <Route
            path="/harbors/view"
            element={
              <PermissionRoute path="/harbors/view">
                <HarborForm />
              </PermissionRoute>
            }
          />

          <Route
            path="/harbors/details"
            element={
              <PermissionRoute path="/harbors/details">
                <HarborDetails />
              </PermissionRoute>
            }
          />

          <Route
            path="/user-feedbacks"
            element={
              <PermissionRoute path="/user-feedbacks">
                <UserFeedbackManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/user-feedbacks/:feedbackId"
            element={
              <PermissionRoute path="/user-feedbacks">
                <UserFeedbackDetailsPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/privacy-policy"
            element={
              <PermissionRoute path="/privacy-policy">
                <PrivacyPolicy />
              </PermissionRoute>
            }
          />
          <Route
            path="/accessibility"
            element={
              <PermissionRoute path="/accessibility">
                <Accessibility />
              </PermissionRoute>
            }
          />
          <Route
            path="/licensing"
            element={
              <PermissionRoute path="/licensing">
                <Licensing />
              </PermissionRoute>
            }
          />
          <Route
            path="/faq"
            element={
              <PermissionRoute path="/faq">
                <FAQ />
              </PermissionRoute>
            }
          />
          <Route
            path="/terms-and-conditions"
            element={
              <PermissionRoute path="/terms-and-conditions">
                <TermsAndConditions />
              </PermissionRoute>
            }
          />
          <Route
            path="/about-us"
            element={
              <PermissionRoute path="/about-us">
                <AboutUs />
              </PermissionRoute>
            }
          />
          <Route
            path="/cookie-consent-settings"
            element={
              <PermissionRoute path="/cookie-consent-settings">
                <CookieConsent />
              </PermissionRoute>
            }
          />
          <Route
            path="/testimonial-management"
            element={
              <PermissionRoute path="/testimonial-management">
                <TestimonialsManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/testimonial/create"
            element={
              <PermissionRoute path="/testimonial/create">
                <TestimonialFormPage mode="create" />
              </PermissionRoute>
            }
          />
          <Route
            path="/testimonial/edit/:id"
            element={
              <PermissionRoute path="/testimonial/edit/:id">
                <TestimonialFormPage mode="edit" />
              </PermissionRoute>
            }
          />
          <Route
            path="/testimonial/view/:id"
            element={
              <PermissionRoute path="/testimonial/view/:id">
                <TestimonialFormPage mode="view" />
              </PermissionRoute>
            }
          />
          <Route
            path="/article-management"
            element={
              <PermissionRoute path="/article-management">
                <ArticlesListing />
              </PermissionRoute>
            }
          />
          <Route
            path="/article-management/create"
            element={
              <PermissionRoute path="/article-management/create">
                <ArticleForm />
              </PermissionRoute>
            }
          />
          <Route
            path="/article-management/edit/:id"
            element={
              <PermissionRoute path="/article-management/edit/:id">
                <ArticleForm />
              </PermissionRoute>
            }
          />
          <Route
            path="/article-management/view/:id"
            element={
              <PermissionRoute path="/article-management/view/:id">
                <ArticleView />
              </PermissionRoute>
            }
          />
          <Route
            path="/article-management/:id/versions"
            element={
              <PermissionRoute path="/article-management/:id/versions">
                <ArticleVersions />
              </PermissionRoute>
            }
          />
          <Route
            path="/addon-management"
            element={
              <PermissionRoute path="/addon-management">
                <AddonManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/promo-management"
            element={
              <PermissionRoute path="/promo-management">
                <PromoManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/promo-management/create"
            element={
              <PermissionRoute path="/promo-management/create">
                <PromoFormPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/promo-management/:promoId/edit"
            element={
              <PermissionRoute path="/promo-management">
                <PromoFormPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/discount-tracking-reports"
            element={
              <PermissionRoute path="/discount-tracking-reports">
                <DiscountTrackingReports />
              </PermissionRoute>
            }
          />
          <Route
            path="/transactions"
            element={
              <PermissionRoute path="/transactions">
                <Transactions />
              </PermissionRoute>
            }
          />
          <Route
            path="/transactions/:id"
            element={
              <PermissionRoute path="/transactions/:id">
                <TransactionDetails />
              </PermissionRoute>
            }
          />
          <Route
            path="/subscription-transactions"
            element={
              <PermissionRoute path="/subscription-transactions">
                <SubscriptionTransactions />
              </PermissionRoute>
            }
          />
          <Route
            path="/subscription-transactions/:id"
            element={
              <PermissionRoute path="/subscription-transactions/:id">
                <SubscriptionTransactionDetails />
              </PermissionRoute>
            }
          />
          <Route
            path="/netvisor/accounting-exports"
            element={
              <PermissionRoute path="/netvisor/accounting-exports">
                <AccountingExports />
              </PermissionRoute>
            }
          />
          <Route
            path="/netvisor/accounting-exports/:id"
            element={
              <PermissionRoute path="/netvisor/accounting-exports/:id">
                <AccountingExportDetails />
              </PermissionRoute>
            }
          />
          <Route
            path="/settlements"
            element={
              <PermissionRoute path="/settlements">
                <Settlements />
              </PermissionRoute>
            }
          />
          <Route
            path="/settlements/:id"
            element={
              <PermissionRoute path="/settlements/:id">
                <SettlementDetails />
              </PermissionRoute>
            }
          />
          <Route
            path="/historical-trends"
            element={
              <PermissionRoute path="/historical-trends">
                <HistoricalTrends />
              </PermissionRoute>
            }
          />
          <Route
            path="/revenue-report"
            element={
              <PermissionRoute path="/revenue-report">
                <RevenueReport />
              </PermissionRoute>
            }
          />
          <Route
            path="/cancellation-reports"
            element={
              <PermissionRoute path="/cancellation-reports">
                <CancellationReports />
              </PermissionRoute>
            }
          />
          <Route
            path="/subscription-management"
            element={
              <PermissionRoute path="/subscription-management">
                <SubscriptionManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/subscription-management/permissions/:subscriptionId"
            element={
              <PermissionRoute path="/subscription-management">
                <SubscriptionPermissionsPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/subscription/payment"
            element={<SubscriptionPayment />}
          />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route
            path="/piers-management"
            element={
              <PermissionRoute path="/piers-management">
                <PierManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/community-management"
            element={
              <PermissionRoute path="/community-management">
                <CommunityManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/dmo-management"
            element={
              <PermissionRoute path="/dmo-management">
                <DMOManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/dmo-management/staffs"
            element={
              <PermissionRoute path="/dmo-management/staffs">
                <DMOStaffManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/dmo-management/staffs/create"
            element={
              <PermissionRoute path="/dmo-management/staffs/create">
                <DMOStaffManagement openCreateOnMount={true} />
              </PermissionRoute>
            }
          />
          <Route
            path="/dmo-management/register"
            element={
              <PermissionRoute path="/dmo-management">
                <RegisterDMOUserPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/dmo-management/register/:id"
            element={
              <PermissionRoute path="/dmo-management">
                <RegisterDMOUserPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/dmo-management/details/:id"
            element={
              <PermissionRoute path="/dmo-management">
                <DMOUserDetails />
              </PermissionRoute>
            }
          />
          <Route
            path="/community/details"
            element={
              <PermissionRoute path="/community/details">
                <CommunityDetails />
              </PermissionRoute>
            }
          />
          <Route
            path="/region-management"
            element={
              <PermissionRoute path="/region-management">
                <RegionListings />
              </PermissionRoute>
            }
          />
          <Route
            path="/icons-management"
            element={
              <PermissionRoute path="/icons-management">
                <IconImagesListings />
              </PermissionRoute>
            }
          />
          <Route
            path="/kpi-management"
            element={
              <PermissionRoute path="/kpi-management">
                <KPIManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/legal-compliance"
            element={
              <PermissionRoute path="/legal-compliance">
                <LegalCompliancePage />
              </PermissionRoute>
            }
          />
          <Route
            path="/theme-routes"
            element={
              <PermissionRoute path="/theme-routes">
                <ThemeRoutesManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/theme-routes/create"
            element={
              <PermissionRoute path="/theme-routes">
                <ThemeRouteFormPage mode="create" />
              </PermissionRoute>
            }
          />
          <Route
            path="/theme-routes/edit/:id"
            element={
              <PermissionRoute path="/theme-routes">
                <ThemeRouteFormPage mode="edit" />
              </PermissionRoute>
            }
          />
          <Route
            path="/theme-routes/view/:id"
            element={
              <PermissionRoute path="/theme-routes">
                <ThemeRouteFormPage mode="view" />
              </PermissionRoute>
            }
          />
          <Route
            path="/theme-routes/:id/versions"
            element={
              <PermissionRoute path="/theme-routes">
                <ThemeRouteVersions />
              </PermissionRoute>
            }
          />
          <Route
            path="/theme-routes/approvals"
            element={
              <PermissionRoute path="/theme-routes">
                <ThemeRouteApprovalsPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/routing-management"
            element={
              <PermissionRoute path="/routing-management">
                <RoutingManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/routing-management/details/:id"
            element={
              <PermissionRoute path="/routing-management/details/:id">
                <RouteDetails />
              </PermissionRoute>
            }
          />
          <Route
            path="/routing-management/manual-route-details/:id"
            element={
              <PermissionRoute path="/routing-management/manual-route-details/:id">
                <ManualRouteDetails />
              </PermissionRoute>
            }
          />
          <Route
            path="/routing-management/version-details/:id"
            element={
              <PermissionRoute path="/routing-management/version-details/:id">
                <VersionDetails />
              </PermissionRoute>
            }
          />
          <Route
            path="/routing-management/manual-route"
            element={
              <PermissionRoute path="/routing-management/manual-route">
                <ManualRoutePage />
              </PermissionRoute>
            }
          />
          {import.meta.env.VITE_ENV === 'staging' && (
            <Route
              path="/routing-management/debugger"
              element={
                <PermissionRoute path="/routing-management/debugger">
                  <RoutingDebugger />
                </PermissionRoute>
              }
            />
          )}
          <Route
            path="/deployements"
            element={
              <PermissionRoute path="/deployements">
                <DeploymentsListing />
              </PermissionRoute>
            }
          />
          <Route
            path="/deployments/:id"
            element={
              <PermissionRoute path="/deployments/:id">
                <DeploymentDetails />
              </PermissionRoute>
            }
          />
          <Route
            path="/routing-management/version-sources/:id"
            element={
              <PermissionRoute path="/routing-management/version-sources/:id">
                <VersionSources />
              </PermissionRoute>
            }
          />
          <Route
            path="/organization-management"
            element={
              <PermissionRoute path="/organization-management">
                <OrganizationManagement />
              </PermissionRoute>
            }
          />

          <Route
            path="/club-booking-management"
            element={
              <PermissionRoute path="/club-booking-management">
                <ClubBookingListings />
              </PermissionRoute>
            }
          />
          <Route
            path="/club-booking-management/:id"
            element={<ClubBookingDetails />}
          />
          <Route
            path="/email-template-management"
            element={
              <PermissionRoute path="/icons-management">
                <EmailTemplateListings />
              </PermissionRoute>
            }
          />
          <Route
            path="/email-template-management/:id"
            element={
              <PermissionRoute path="/icons-management">
                <EmailTemplateDetails />
              </PermissionRoute>
            }
          />
          <Route
            path="/template-layout-management"
            element={
              <PermissionRoute path="/template-layout-management">
                <EmailLayoutListing />
              </PermissionRoute>
            }
          />
          <Route
            path="/template-layout-management/view/:id"
            element={
              <PermissionRoute path="/template-layout-management">
                <EmailLayoutView />
              </PermissionRoute>
            }
          />
          <Route
            path="/notification-template-management"
            element={
              <PermissionRoute path="/icons-management">
                <NotificationTemplateListings />
              </PermissionRoute>
            }
          />
          <Route
            path="/notification-template-management/:id"
            element={
              <PermissionRoute path="/icons-management">
                <NotificationTemplateDetails />
              </PermissionRoute>
            }
          />
          <Route
            path="/system-announcements"
            element={
              // <PermissionRoute path="/system-announcements">
              <SystemAnnouncementsManagement />
              // </PermissionRoute>
            }
          />
          <Route
            path="/system-announcements/archived"
            element={
              <PermissionRoute path="/system-announcements">
                <SystemAnnouncementsArchivedManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/system-announcements/create"
            element={
              <PermissionRoute path="/system-announcements">
                <SystemAnnouncementsForm mode="create" />
              </PermissionRoute>
            }
          />
          <Route
            path="/system-announcements/edit/:id"
            element={
              <PermissionRoute path="/system-announcements">
                <SystemAnnouncementsForm mode="edit" />
              </PermissionRoute>
            }
          />
          <Route
            path="/system-announcements/view/:id"
            element={
              <PermissionRoute path="/system-announcements">
                <SystemAnnouncementsForm mode="view" />
              </PermissionRoute>
            }
          />
          <Route
            path="/system-announcements/recipients/:id"
            element={
              <PermissionRoute path="/system-announcements">
                <SystemAnnouncementsRecipients />
              </PermissionRoute>
            }
          />
          <Route
            path="/club-resources/:resourceId/shifts"
            element={
              <PermissionRoute path="/club-resources/:resourceId/shifts">
                <PortWatchShiftsDetails />
              </PermissionRoute>
            }
          />

          <Route
            path="/club-management/:clubId/annual-inspections/create"
            element={
              <PermissionRoute path="/club-management/:clubId/annual-inspections/create">
                <AnnualInspectionPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/club-management/:clubId/annual-inspections/:resourceId"
            element={
              <PermissionRoute path="/club-management/:clubId/annual-inspections/:resourceId">
                <AnnualInspectionPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/advertise-management"
            element={
              <PermissionRoute path="/advertise-management">
                <AdvertiseManagement />
              </PermissionRoute>
            }
          />

          <Route
            path="/advertise/create"
            element={
              <PermissionRoute path="/advertise/create">
                <AdvertiseForm mode="create" />
              </PermissionRoute>
            }
          />
          <Route
            path="/advertise/edit/:id"
            element={
              <PermissionRoute path="/advertise/edit/:id">
                <AdvertiseForm mode="edit" />
              </PermissionRoute>
            }
          />

          <Route
            path="/product-vat-config"
            element={
              <PermissionRoute path="/product-vat-config">
                <ProductVatConfigManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/product-vat-config/create"
            element={
              <PermissionRoute path="/product-vat-config/create">
                <ProductVatConfigForm mode="create" />
              </PermissionRoute>
            }
          />
          <Route
            path="/service-moderation"
            element={
              <PermissionRoute path="/service-moderation">
                <ServiceModerationManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/service-moderation/details/:id"
            element={
              <PermissionRoute path="/service-moderation/details/:id">
                <ServiceModerationDetails />
              </PermissionRoute>
            }
          />
          <Route
            path="/product-vat-config/edit/:id"
            element={
              <PermissionRoute path="/product-vat-config/edit/:id">
                <ProductVatConfigForm mode="edit" />
              </PermissionRoute>
            }
          />
          <Route
            path="/domestic-vat-config"
            element={
              <PermissionRoute path="/product-vat-config">
                <DomesticVatConfigManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/domestic-vat-config/create"
            element={
              <PermissionRoute path="/product-vat-config/create">
                <DomesticVatConfigForm mode="create" />
              </PermissionRoute>
            }
          />
          <Route
            path="/domestic-vat-config/edit/:id"
            element={
              <PermissionRoute path="/product-vat-config/edit/:id">
                <DomesticVatConfigForm mode="edit" />
              </PermissionRoute>
            }
          />
          <Route
            path="/business-entity-vat-config"
            element={
              <PermissionRoute path="/product-vat-config">
                <BusinessEntityVatConfigManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/business-entity-vat-config/create"
            element={
              <PermissionRoute path="/product-vat-config/create">
                <BusinessEntityVatConfigForm mode="create" />
              </PermissionRoute>
            }
          />
          <Route
            path="/business-entity-vat-config/edit/:id"
            element={
              <PermissionRoute path="/product-vat-config/edit/:id">
                <BusinessEntityVatConfigForm mode="edit" />
              </PermissionRoute>
            }
          />

          <Route
            path="/penalty-config"
            element={
              <PermissionRoute path="/penalty-config">
                <PenaltyConfigManagement />
              </PermissionRoute>
            }
          />

          <Route
            path="/psp-fee-config"
            element={
              <PermissionRoute path="/psp-fee-config">
                <PspFeeConfigManagement />
              </PermissionRoute>
            }
          />

          <Route
            path="/booking-reminder-configuration"
            element={
              <PermissionRoute path="/booking-reminder-configuration">
                <BookingReminderConfiguration />
              </PermissionRoute>
            }
          />

          <Route
            path="/configuration/commission"
            element={
              <PermissionRoute path="/configuration/commission">
                <CommissionPolicyConfiguration />
              </PermissionRoute>
            }
          />

          <Route
            path="/configuration/subscription-trial"
            element={
              <PermissionRoute path="/configuration/subscription-trial">
                <SubscriptionTrialConfiguration />
              </PermissionRoute>
            }
          />

          <Route
            path="/configuration/guest-visibility"
            element={
              <PermissionRoute path="/configuration/guest-visibility">
                <GuestVisibilityConfiguration />
              </PermissionRoute>
            }
          />

          <Route
            path="/configuration/guest-booking-governance"
            element={
              <PermissionRoute path="/configuration/guest-booking-governance">
                <GuestBookingGovernanceConfiguration />
              </PermissionRoute>
            }
          />

          <Route
            path="/configuration/entitlement-grants"
            element={
              <PermissionRoute path="/configuration/entitlement-grants">
                <EntitlementGrantManagement />
              </PermissionRoute>
            }
          />

          <Route
            path="/configuration/entitlement-grants/create"
            element={
              <PermissionRoute path="/configuration/entitlement-grants">
                <EntitlementGrantConfiguration />
              </PermissionRoute>
            }
          />

          <Route
            path="/configuration/entitlement-grants/:id"
            element={
              <PermissionRoute path="/configuration/entitlement-grants">
                <EntitlementGrantConfiguration />
              </PermissionRoute>
            }
          />

          <Route
            path="/harbors/:harborId/commission"
            element={
              <PermissionRoute path="/harbors/:harborId/commission">
                <HarborCommissionPolicyPage />
              </PermissionRoute>
            }
          />

          <Route
            path="/service-providers/:serviceProviderId/commission"
            element={
              <PermissionRoute path="/service-providers/:serviceProviderId/commission">
                <ServiceProviderCommissionPolicyPage />
              </PermissionRoute>
            }
          />

          <Route path="/event/create" element={<EventForm mode="create" />} />
          <Route path="/event/edit/:id" element={<EventForm mode="edit" />} />

          <Route path="/service-management" element={<ServiceManagement />} />
          <Route
            path="/services/create"
            element={<ServiceFormPage mode="create" />}
          />
          <Route
            path="/services/edit/:id"
            element={<ServiceFormPage mode="edit" />}
          />
          <Route
            path="/services/view/:id"
            element={<ServiceFormPage mode="view" />}
          />
          <Route
            path="/service-bookings/:bookingId"
            element={
              <PermissionRoute path="/service-management">
                <ServiceBookingDetails />
              </PermissionRoute>
            }
          />
          <Route
            path="/service-bookings/:bookingId/edit"
            element={
              <PermissionRoute path="/service-management">
                <ServiceBookingDetails />
              </PermissionRoute>
            }
          />
          <Route
            path="/service-providers/create"
            element={
              <PermissionRoute path="/service-providers/create">
                <ServiceProviderFormPage mode="create" />
              </PermissionRoute>
            }
          />
          <Route
            path="/service-providers/edit/:id"
            element={
              <PermissionRoute path="/service-providers/edit/:id">
                <ServiceProviderFormPage mode="edit" />
              </PermissionRoute>
            }
          />
          <Route
            path="/service-providers/details"
            element={
              <PermissionRoute path="/service-providers/details">
                <ServiceProviderDetailsView />
              </PermissionRoute>
            }
          />
          <Route
            path="/service-providers/requests"
            element={
              <PermissionRoute path="/service-providers/requests">
                <ServiceProviderRequests />
              </PermissionRoute>
            }
          />
          <Route
            path="/kyc-management"
            element={
              <PermissionRoute path="/kyc-management">
                <KYCManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/kyc-management/details"
            element={
              <PermissionRoute path="/kyc-management/details">
                <KYCDetailsView />
              </PermissionRoute>
            }
          />
          <Route
            path="/helpdesk-faq"
            element={
              <PermissionRoute path="/helpdesk-faq">
                <HelpdeskFAQListing />
              </PermissionRoute>
            }
          />
          <Route
            path="/helpdesk-faq/create"
            element={
              <PermissionRoute path="/helpdesk-faq/create">
                <HelpdeskFAQForm />
              </PermissionRoute>
            }
          />
          <Route
            path="/helpdesk-faq/edit/:id"
            element={
              <PermissionRoute path="/helpdesk-faq/edit/:id">
                <HelpdeskFAQForm />
              </PermissionRoute>
            }
          />
          <Route
            path="/helpdesk-faq/view/:id"
            element={
              <PermissionRoute path="/helpdesk-faq/view/:id">
                <HelpdeskFAQView />
              </PermissionRoute>
            }
          />
          <Route
            path="/platform-setting"
            element={
              <PermissionRoute path="/platform-setting">
                <PlatformSettingListing />
              </PermissionRoute>
            }
          />
          <Route
            path="/platform-setting/edit/:id"
            element={
              <PermissionRoute path="/platform-setting">
                <PlatformSettingForm />
              </PermissionRoute>
            }
          />
          <Route
            path="/list-value-management"
            element={
              <PermissionRoute path="/list-value-management">
                <ListValueManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="/list-value-management/create/:type"
            element={
              <PermissionRoute path="/list-value-management/create/:type">
                <ListValueFormPage mode="create" />
              </PermissionRoute>
            }
          />
          <Route
            path="/list-value-management/edit/:type/:id"
            element={
              <PermissionRoute path="/list-value-management/edit/:type/:id">
                <ListValueFormPage mode="edit" />
              </PermissionRoute>
            }
          />

          <Route
            path="/synthetic-canary"
            element={
              <PermissionRoute path="/synthetic-canary">
                <SyntheticCanary />
              </PermissionRoute>
            }
          />
          <Route
            path="/configuration/environment-variables"
            element={
              <PermissionRoute path="/configuration/environment-variables">
                <EnvironmentVariablesListing />
              </PermissionRoute>
            }
          />
          <Route
            path="/configuration/environment-variables/edit/:id"
            element={
              <PermissionRoute path="/configuration/environment-variables/edit/:id">
                <EnvironmentVariableForm />
              </PermissionRoute>
            }
          />
          <Route path="/category-management" element={<CategoryManagement />} />
          <Route path="/qa-management" element={<QAManagement />} />
          <Route path="/misc" element={<Misc />} />
          <Route
            path="/account/home/user-profile"
            element={<AccountUserProfilePage />}
          />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route path="/user-kyc" element={<UserKYC />} />
          <Route path="/user-kyc/add" element={<AddKYC />} />
          <Route path="/user-kyc/edit/:id" element={<EditKYC />} />
          <Route path="/user-kyc/view/:id" element={<ViewKYC />} />
        </Route>
      </Route>
      <Route
        path="/community-invitations/accept"
        element={<CommunityInvitationAcceptPage />}
      />
      <Route path="error/*" element={<ErrorsRouting />} />
      <Route path="auth/*" element={<AuthPage />} />
      <Route path="/dmo-registration" element={<PublicDMORegistrationPage />} />
      <Route path="*" element={<Navigate to="/error/404" />} />
    </Routes>
  );
};

export { AppRoutingSetup };
